import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchReceivables, exportXlsx, listUsers } from "../api";
import ManagerCell from "../components/ManagerCell";
import {
  PageHeader, Card, Stat, Field, Select, Segmented, SortTh, sortRows,
  Loading, ErrorState, EmptyState,
} from "../components/ui";
import { money0, money, fmtDate, daysSince, num } from "../lib/format";

const BUCKETS = [
  { value: "all", label: "All",     test: () => true },
  { value: "a",   label: "0–30",    test: (d) => d <= 30 },
  { value: "b",   label: "31–60",   test: (d) => d > 30 && d <= 60 },
  { value: "c",   label: "61–90",   test: (d) => d > 60 && d <= 90 },
  { value: "d",   label: "90+",     test: (d) => d > 90 },
];

const ageClass = (d) =>
  d > 90 ? "badge--neg" : d > 60 ? "badge--warn" : d > 30 ? "badge--info" : "badge--pos";

export default function Receivables({ username }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [project, setProject] = useState("");
  const [bucket, setBucket] = useState("all");
  const [sort, setSort] = useState({ key: "date", dir: "asc" });
  const isFd = String(username).toLowerCase() === "fd";
  const [manager, setManager] = useState("");
  const [allManagers, setAllManagers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    // Usernames are occasionally stored as "am:something" - the API wants the stem.
    fetchReceivables(String(username).split(":")[0])
      .then((data) => setRows(data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!isFd) return;
    listUsers()
      .then((us) => setAllManagers(us.map((u) => u.username).filter((u) => u !== "fd").sort()))
      .catch(() => {});
  }, [isFd]);

  /** Swap one row in place after fd reallocates it. */
  const applyReallocation = (id, updated) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated } : r)));

  const managers = useMemo(
    () => [...new Set(
      [...rows.map((r) => r.username), manager].filter(Boolean)
    )].sort(),
    [rows, manager]
  );

  const projects = useMemo(
    () => [...new Set(
      rows.filter((r) => !manager || r.username === manager)
          .map((r) => r.project_name).filter(Boolean)
    )].sort(),
    [rows, manager]
  );

  const filtered = useMemo(() => {
    const test = BUCKETS.find((b) => b.value === bucket)?.test || (() => true);
    return rows.filter(
      (r) => (!project || r.project_name === project)
          && (!manager || r.username === manager)
          && test(daysSince(r.date))
    );
  }, [rows, project, manager, bucket]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, sort, {
        date: (r) => new Date(r.date).getTime() || 0,
        amount: (r) => num(r.amount),
        days: (r) => daysSince(r.date),
      }),
    [filtered, sort]
  );

  const byYear = useMemo(() => {
    const groups = new Map();
    sorted.forEach((r) => {
      const y = new Date(r.date).getFullYear();
      const key = Number.isNaN(y) ? "—" : String(y);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    return [...groups.entries()];
  }, [sorted]);

  const stats = useMemo(() => {
    const total = filtered.reduce((s, r) => s + num(r.amount), 0);
    const ages = filtered.map((r) => daysSince(r.date));
    const over90 = filtered.filter((r) => daysSince(r.date) > 90);
    return {
      total,
      count: filtered.length,
      oldest: ages.length ? Math.max(...ages) : 0,
      avg: ages.length ? Math.round(ages.reduce((s, d) => s + d, 0) / ages.length) : 0,
      over90Amount: over90.reduce((s, r) => s + num(r.amount), 0),
      over90Count: over90.length,
    };
  }, [filtered]);

  const scope = manager ? manager.toUpperCase() : String(username).toUpperCase();
  const bucketLabel = BUCKETS.find((b) => b.value === bucket)?.label || "All";

  const clearAll = () => { setProject(""); setManager(""); setBucket("all"); };

  const handleExport = async () => {
    setExporting(true);
    setNotice("");
    try {
      await exportXlsx({
        filename: `Receivables ${scope} ${new Date().toISOString().slice(0, 10)}`,
        sheet: "Receivables",
        title: `Receivables — ${scope}${project ? ` — ${project}` : ""}`
             + (bucket !== "all" ? ` — ${bucketLabel} days` : ""),
        columns: [
          ...(isFd ? [{ key: "username", label: "Manager", type: "text" }] : []),
          { key: "date",         label: "Date",     type: "date" },
          { key: "project_name", label: "Project",  type: "text" },
          { key: "client",       label: "Client",   type: "text" },
          { key: "amount",       label: "Amount",   type: "money" },
          { key: "days",         label: "Days outstanding", type: "number" },
          { key: "comments",     label: "Comments", type: "text" },
        ],
        rows: sorted.map((r) => ({ ...r, days: daysSince(r.date) })),
        totals: { amount: stats.total },
      });
    } catch (e) {
      setNotice(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <main className="page"><Loading label="Loading receivables…" /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  return (
    <main className="page">
      <PageHeader
        title="Receivables"
        subtitle={`Outstanding invoices for ${String(username).toUpperCase()}`}
      >
        {isFd && (
          <Field label="Manager">
            <Select
              value={manager}
              onChange={(v) => { setManager(v); setProject(""); }}
              options={[
                { value: "", label: "All managers" },
                ...managers.map((m) => ({ value: m, label: m.toUpperCase() })),
              ]}
              style={{ minWidth: 130 }}
            />
          </Field>
        )}
        <Field label="Project">
          <Select
            value={project}
            onChange={setProject}
            options={[{ value: "", label: "All projects" }, ...projects.map((p) => ({ value: p, label: p }))]}
            style={{ maxWidth: 250 }}
          />
        </Field>
        <Field label="Age">
          <Segmented
            value={bucket}
            onChange={setBucket}
            options={BUCKETS.map(({ value, label }) => ({ value, label }))}
          />
        </Field>
        <button className="btn btn--danger" onClick={clearAll}>Clear all</button>
        <button className="btn" onClick={handleExport} disabled={exporting || !sorted.length}>
          {exporting ? "Preparing…" : "Export to Excel"}
        </button>
      </PageHeader>

      <div className="stack">
        {notice && <div className="notice notice--warn">{notice}</div>}
        <div className="stat-grid">
          <Stat label="Outstanding" accent="var(--accent)" value={money0(stats.total)}
                meta={`${stats.count} invoice${stats.count === 1 ? "" : "s"}`} />
          <Stat label="Over 90 days" accent={stats.over90Count ? "var(--neg)" : "var(--pos)"}
                value={money0(stats.over90Amount)}
                meta={`${stats.over90Count} invoice${stats.over90Count === 1 ? "" : "s"}`} />
          <Stat label="Oldest" accent="var(--warn)" value={`${stats.oldest}d`} plain
                meta="days outstanding" />
          <Stat label="Average age" accent="var(--info)" value={`${stats.avg}d`} plain
                meta="across the selection" />
        </div>

        <Card title="Invoices" note={`${sorted.length} row${sorted.length === 1 ? "" : "s"}`} flush>
          {!sorted.length ? (
            <EmptyState title="Nothing outstanding" hint="No receivables match this selection." />
          ) : (
            <div className="tbl-wrap tbl-wrap--flush">
              <table className="tbl">
                <thead>
                  <tr>
                    {isFd && (
                      <SortTh id="username" sort={sort} setSort={setSort} style={{ width: 78 }}>Manager</SortTh>
                    )}
                    <SortTh id="date" sort={sort} setSort={setSort} style={{ width: 108 }}>Date</SortTh>
                    <SortTh id="project_name" sort={sort} setSort={setSort} style={{ minWidth: 200 }}>Project</SortTh>
                    <SortTh id="client" sort={sort} setSort={setSort} style={{ minWidth: 160 }}>Client</SortTh>
                    <SortTh id="amount" sort={sort} setSort={setSort} numeric style={{ width: 130 }}>Amount</SortTh>
                    <SortTh id="days" sort={sort} setSort={setSort} numeric style={{ width: 96 }}>Age</SortTh>
                    <th className="col-flex" style={{ minWidth: 220 }}>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {byYear.map(([year, items]) => (
                    <React.Fragment key={year}>
                      {items.map((r, i) => {
                        const age = daysSince(r.date);
                        return (
                          <tr key={r.id ?? `${year}-${i}`} className={i === 0 ? "group-start" : ""}>
                            {isFd && (
                              <td>
                                <ManagerCell
                                  row={r}
                                  dataset="receivables"
                                  label={r.project_name}
                                  managers={allManagers}
                                  editable
                                  onChange={applyReallocation}
                                />
                              </td>
                            )}
                            <td className="nowrap">{fmtDate(r.date)}</td>
                            <td className="truncate" title={r.project_name}>{r.project_name}</td>
                            <td className="truncate" title={r.client}>
                              {r.client || <span className="dash">—</span>}
                            </td>
                            <td className="num">{money(r.amount)}</td>
                            <td className="num">
                              <span className={`badge ${ageClass(age)}`}>{age}d</span>
                            </td>
                            <td className="truncate" title={r.comments}>
                              {r.comments || <span className="dash">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="band subtotal-row">
                        <td colSpan={isFd ? 4 : 3}>Total {year}</td>
                        <td className="num">
                          {money(items.reduce((s, r) => s + num(r.amount), 0))}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={isFd ? 4 : 3}>Total outstanding</td>
                    <td className="num">{money(stats.total)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
